import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2 } from "lucide-react";

interface LoadingStateProps {
  username: string;
}

const STEPS = [
  "Connecting to Reddit API...",
  "Fetching account statistics...",
  "Evaluating Karma score...",
  "Verifying eligibility...",
];

export default function LoadingState({ username }: LoadingStateProps) {
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const intervals = [1200, 1500, 1000, 1200];
    let step = 0;

    const runNextStep = () => {
      if (step < STEPS.length - 1) {
        step++;
        setCurrentStep(step);
        setTimeout(runNextStep, intervals[step]);
      }
    };

    const timer = setTimeout(runNextStep, intervals[0]);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="w-full max-w-lg mx-auto bg-white border-2 border-indigo-100 rounded-3xl p-8 text-slate-800 shadow-2xl shadow-indigo-100/50 relative overflow-hidden">
      <div className="text-center mb-8">
        <h3 className="text-xl font-black mb-2 text-[#3F51B5]">Analyzing Profile</h3>
        <p className="text-slate-600 text-sm font-medium">
          Checking eligibility for <span className="text-amber-600 font-bold">u/{username}</span>
        </p>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-slate-100 h-2.5 rounded-full mb-8 overflow-hidden">
        <motion.div
          className="h-full bg-gradient-to-r from-[#FFC107] to-[#3F51B5]"
          initial={{ width: "0%" }}
          animate={{ width: `${((currentStep + 1) / STEPS.length) * 100}%` }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
        />
      </div>

      {/* Steps List */}
      <div className="space-y-4">
        {STEPS.map((stepText, index) => {
          const isDone = index < currentStep;
          const isLoading = index === currentStep;

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`flex items-center justify-between p-4 rounded-2xl border-2 transition-all duration-300 ${
                isLoading
                  ? "bg-indigo-50/60 border-[#3F51B5] text-slate-900 shadow-sm"
                  : isDone
                  ? "bg-slate-50 border-slate-200 text-slate-700"
                  : "bg-transparent border-transparent text-slate-400"
              }`}
            >
              <div className="flex items-center gap-3">
                {isDone ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                ) : isLoading ? (
                  <Loader2 className="w-5 h-5 text-[#3F51B5] animate-spin shrink-0" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-slate-200 shrink-0" />
                )}
                <span className={`text-sm font-bold ${isLoading ? "text-[#3F51B5]" : ""}`}>
                  {stepText}
                </span>
              </div>

              {isDone && (
                <span className="text-[10px] text-emerald-700 font-extrabold uppercase tracking-wider bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                  Success
                </span>
              )}
              {isLoading && (
                <span className="text-[10px] text-amber-800 font-extrabold uppercase tracking-wider bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full animate-pulse">
                  Analyzing
                </span>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
